import React from "react";
import { Link, Redirect, RouteComponentProps } from "react-router-dom";
import { MainContext } from "../../contexts/MainContext";
import "./Users.css";
import "../../App.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import SearchInput from "../../components/SearchInput";
import PaginatedTable from "../../components/Table/PaginatedTable";
import { RowData } from "../../Types";
import { apiRequest } from "../../functions/requestMulti";
import {
  ListCacheEntry,
  restoreListState,
  restoreScrollPosition,
  saveListState,
} from "../../functions/listStateCache";
import debounce from "lodash.debounce";

type Props = {};
type ComposedProps = Props &
  RouteComponentProps<{
    clubId: string;
  }>;

export default class Users extends React.Component<
  ComposedProps,
  {
    clubId: string;
    club: any;
    clubLoaded: boolean;
    userSearchVal: string;
    users: Array<any>;
    filteredUsers: Array<any>;
    usersLoaded: boolean;
    selectedUser: any;
    redirect: string | null;
    userTableArr: Array<RowData>;
    page: number;
    pageSize: number;
    totalUsers: number;
    sortBy: string;
    sortOrder: "asc" | "desc";
  }
> {
  static contextType = MainContext;

  private cacheKey: string;
  private restored: ListCacheEntry | null;
  private leavingToDetail = false;
  private cancelScroll: (() => void) | null = null;
  private mounted = false;
  private rootRef = React.createRef<HTMLDivElement>();

  constructor(props: ComposedProps) {
    super(props);
    this.cacheKey = props.match.url;
    this.restored = restoreListState(this.cacheKey);
    this.state = {
      clubId: this.props.match.params.clubId,
      club: {},
      clubLoaded: false,
      userSearchVal: "",
      users: [],
      filteredUsers: [],
      usersLoaded: false,
      selectedUser: {},
      userTableArr: [],
      page: 1,
      pageSize: 10,
      totalUsers: 0,
      sortBy: "lName",
      sortOrder: "asc",
      // Returning from a user's detail page: render the previous page, filters
      // and rows straight away instead of resetting to page 1 with skeletons.
      ...(this.restored ? this.restored.state : {}),
      redirect: null,
    };
  }

  componentDidMount = () => {
    this.mounted = true;
    if (this.restored) {
      this.cancelScroll = restoreScrollPosition(
        this.restored,
        this.rootRef.current
      );
    }
    // Also runs when restoring: requestUsers never flips usersLoaded back to
    // false, so this is a silent refresh that swaps the rows in on arrival.
    this.requestUsers();
  };

  componentWillUnmount = () => {
    this.mounted = false;
    if (this.cancelScroll) this.cancelScroll();
    this.debounceRequest.cancel();
    if (this.leavingToDetail) {
      saveListState(
        this.cacheKey,
        {
          userSearchVal: this.state.userSearchVal,
          users: this.state.users,
          userTableArr: this.state.userTableArr,
          page: this.state.page,
          pageSize: this.state.pageSize,
          totalUsers: this.state.totalUsers,
          sortBy: this.state.sortBy,
          sortOrder: this.state.sortOrder,
          usersLoaded: true,
        },
        this.rootRef.current
      );
    }
  };

  requestUsers = async () => {
    await apiRequest("api/club/users", "GET", {
      targetUserId: this.context.selectedUserId,
      clubId: this.state.clubId,
      query: this.state.userSearchVal,
      page: this.state.page,
      hitsPerPage: this.state.pageSize,
      sortBy: this.state.sortBy,
      sortOrder: this.state.sortOrder,
    })
      .then(this.handleUsers)
      .catch(this.handleError);
  };

  handleUsers = ({ data }: any) => {
    // apiRequest retries for a few seconds, so a response can land after the
    // screen is gone.
    if (!this.mounted) return;
    this.setState({ usersLoaded: true });
    if (data.success) {
      // Updated to match the new API response structure
      const responseData = data.data;
      const userData = responseData.users;
      const totalCount = responseData.totalCount;

      const userTableArr = userData.map((user: any) => {
        return {
          cells: [
            {
              value: user.membershipNumber,
              sort: user.membershipNumber,
              search: user.membershipNumber,
            },
            {
              value: user.fName,
              sort: user.fName,
              search: `${user.fName} ${user.lName}`,
            },
            {
              value: user.lName,
              sort: user.lName,
              search: user.lName,
            },
            {
              value: user.email,
              sort: user.email,
              search: user.email,
            },
            {
              value: user.role === "Owner" ? "Admin" : "Mitglied",
              sort: user.role === "Owner" ? "Admin" : "Mitglied",
              search: user.role === "Owner" ? "Admin" : "Mitglied",
            },
          ],
          onClick: `/club-settings/${this.state.clubId}/users/${user.id}`,
        };
      });
      this.setState({
        users: userData,
        userTableArr,
        totalUsers: totalCount,
      });
    }
  };

  handleChangeClubSearchVal = (val: any) => {
    this.setState({ userSearchVal: val, page: 1 }, this.debounceRequest);
  };

  debounceRequest = debounce(() => {
    this.requestUsers();
  }, 500);

  handlePageChange = (newPage: number) => {
    this.setState({ page: newPage }, this.requestUsers);
  };

  handlePageSizeChange = (newPageSize: number) => {
    this.setState({ pageSize: newPageSize, page: 1 }, this.requestUsers);
  };

  handleSortChange = (sortKey: string) => {
    const newSortOrder =
      this.state.sortBy === sortKey && this.state.sortOrder === "asc"
        ? "desc"
        : "asc";

    this.setState(
      {
        sortBy: sortKey,
        sortOrder: newSortOrder,
        page: 1, // Reset to first page when sorting changes
      },
      this.requestUsers
    );
  };

  handleUserClick = (userData: any) => {
    this.props.history.push({
      pathname: `/club-settings/${this.state.clubId}/users/${userData.id}`,
    });
  };

  handleRedirect = (redirect: string) => {
    // Only a row click leaves a snapshot behind - navigating away via the
    // sidebar must still give a fresh list on the way back.
    this.leavingToDetail = true;
    this.props.history.push({
      pathname: redirect,
    });
  };

  handleError = (err: any) => {
    console.error(err);
  };

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }

    return (
      <>
        <div className="subscreen-main-container" ref={this.rootRef}>
          <div className="subscreen-box-container">
            <SearchInput
              name="course-search-input"
              placeholder="Nutzersuche"
              value={this.state.userSearchVal}
              onChange={this.handleChangeClubSearchVal}
            />
            <div className="inline-block">
              <Link
                className="create-btn"
                to={`/club-settings/${this.state.clubId}/inviteUsers`}
              >
                <div className="create-icon-container">
                  <FontAwesomeIcon icon={faPlusCircle} size="1x" />
                </div>
                Mitglied einladen
              </Link>
            </div>
            <PaginatedTable
              header={[
                {
                  title: "Mitgliedsnummer",
                  displayWidth: 450,
                  sort: "membershipNumber",
                },
                { title: "Vorname", sort: "fName" },
                { title: "Nachname", sort: "lName" },
                { title: "E-Mail", displayWidth: 950, sort: "email" },
                { title: "Rechte", displayWidth: 600 },
              ]}
              data={this.state.userTableArr}
              loaded={this.state.usersLoaded}
              skeletonRows={10}
              onClick={this.handleRedirect}
              defaultSortDirection={this.state.sortOrder}
              defaultSortKey={this.state.sortBy}
              page={this.state.page}
              pageSize={this.state.pageSize}
              entryCount={this.state.totalUsers}
              onPageChange={this.handlePageChange}
              onPageSizeChange={this.handlePageSizeChange}
              onSortChange={this.handleSortChange}
            />
          </div>
        </div>
      </>
    );
  }
}
